import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Plug, RefreshCw, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { toast } from '@/lib/toast';
import {
  useDeleteEmailAccount,
  useEmailAccount,
  useEmailFolders,
  usePollEmail,
  useSaveEmailAccount,
  useTestEmailAccount,
} from './useEmailAccount';

const BLANK = {
  host: '',
  port: 993,
  use_ssl: true,
  username: '',
  folder: 'INBOX',
  enabled: true,
  delete_after_accept: true,
};

/**
 * IMAP capture mailbox.
 *
 * Unread mail here is read every few minutes, parsed into proposals, and left
 * in the Inbox for review. IMAP has no token flow, so the password is stored
 * encrypted but recoverable: which is why this asks for an app password and
 * says so rather than leaving the user to assume otherwise.
 */
export function EmailInboxPanel() {
  const account = useEmailAccount();
  const save = useSaveEmailAccount();
  const remove = useDeleteEmailAccount();
  const test = useTestEmailAccount();
  const poll = usePollEmail();

  const [form, setForm] = useState(BLANK);
  const [password, setPassword] = useState('');

  const data = account.data;
  const foldersQuery = useEmailFolders(Boolean(data?.configured));
  const folderOptions =
    foldersQuery.data?.folders ??
    (data?.configured && form.folder ? [form.folder] : []);
  const showFolderSelect = Boolean(data?.configured && folderOptions.length > 0);

  // Seed the form once the saved config arrives. Anything typed since stays.
  useEffect(() => {
    if (!data?.configured) return;
    setForm({
      host: data.host ?? '',
      port: data.port,
      use_ssl: data.use_ssl,
      username: data.username ?? '',
      folder: data.folder,
      enabled: data.enabled,
      delete_after_accept: data.delete_after_accept,
    });
  }, [data?.configured, data?.host, data?.port, data?.use_ssl, data?.username, data?.folder, data?.enabled, data?.delete_after_accept]);

  const set = <K extends keyof typeof BLANK>(key: K, value: (typeof BLANK)[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleTest = () => {
    test.mutate(
      { folder: form.folder },
      {
        onSuccess: (result) => {
          if (!result.folders.includes(form.folder) && result.folders.length > 0) {
            const inbox = result.folders.find((f) => f.toUpperCase() === 'INBOX');
            set('folder', inbox ?? result.folders[0]!);
          }
        },
      },
    );
  };

  if (!data) {
    return <div className="h-48 animate-pulse rounded-lg bg-surface-2" aria-hidden />;
  }

  const canSave =
    form.host.trim() !== '' &&
    form.username.trim() !== '' &&
    (data.configured || password !== '');

  const submit = () => {
    save.mutate(
      { ...form, password: password || undefined },
      {
        onSuccess: () => {
          setPassword('');
          toast.show('Mailbox saved');
        },
      },
    );
  };

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-border bg-surface-1 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {data.configured ? (
          <p className="flex items-center gap-2 text-small text-success">
            <Check size={14} strokeWidth={2} aria-hidden />
            Connected to {data.username}
          </p>
        ) : (
          <p className="text-small text-text-tertiary">No mailbox connected.</p>
        )}

        {data.configured && (
          <label className="inline-flex min-h-touch items-center gap-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => {
                set('enabled', e.target.checked);
                save.mutate({ ...form, enabled: e.target.checked });
              }}
              className="size-icon accent-[var(--accent)]"
            />
            <span className="text-small text-text-secondary">Check for mail every 5 minutes</span>
          </label>
        )}
      </div>

      {/* A past failure, shown as a record rather than a live alarm. */}
      {data.last_error && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-surface-2 p-3">
          <AlertTriangle
            size={14}
            strokeWidth={1.5}
            className="mt-0.5 shrink-0 text-warning"
            aria-hidden
          />
          <p className="text-small text-text-secondary">{data.last_error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="IMAP server"
          value={form.host}
          onChange={(e) => set('host', e.target.value)}
          placeholder="imap.fastmail.com"
          autoComplete="off"
        />
        <Field
          label="Port"
          type="number"
          value={form.port}
          onChange={(e) => set('port', Number(e.target.value))}
        />
        <Field
          label="Address"
          value={form.username}
          onChange={(e) => set('username', e.target.value)}
          hint="A mailbox used only for capture, everything unread in it gets read."
          autoComplete="off"
        />
        <Field
          label="App password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={data.configured ? 'Saved. Leave blank to keep' : ''}
          hint="Use an app password, not your account password. IMAP needs a password it can replay, so this is stored encrypted but recoverable."
          autoComplete="new-password"
        />
        {showFolderSelect ? (
          <div className="flex flex-col gap-2">
            <label htmlFor="email-folder" className="text-small font-medium text-text-secondary">
              Folder
            </label>
            <select
              id="email-folder"
              value={form.folder}
              onChange={(e) => {
                const folder = e.target.value;
                set('folder', folder);
                save.mutate({ ...form, folder, password: password || undefined });
                test.mutate({ folder });
              }}
              className="min-h-touch rounded border border-border bg-surface-1 px-3 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
            >
              {folderOptions.map((folder) => (
                <option key={folder} value={folder}>
                  {folder}
                </option>
              ))}
            </select>
            <p className="text-small text-text-tertiary">
              Folder saves automatically when changed. Counts update after each test.
            </p>
          </div>
        ) : (
          <Field
            label="Folder"
            value={form.folder}
            onChange={(e) => set('folder', e.target.value)}
            hint="Test the connection to load folders from your server."
          />
        )}
      </div>

      <label className="inline-flex min-h-touch items-center gap-2">
        <input
          type="checkbox"
          checked={form.use_ssl}
          onChange={(e) => set('use_ssl', e.target.checked)}
          className="size-icon accent-[var(--accent)]"
        />
        <span className="text-small text-text-secondary">Connect over TLS</span>
      </label>

      <div className="rounded-lg border border-border bg-surface-2 p-3">
        <label className="inline-flex min-h-touch items-start gap-2">
          <input
            type="checkbox"
            checked={form.delete_after_accept}
            onChange={(e) => set('delete_after_accept', e.target.checked)}
            className="mt-1 size-icon shrink-0 accent-[var(--accent)]"
          />
          <span className="text-small text-text-secondary">
            <span className="font-medium text-text">
              Delete mail once its tasks are approved
            </span>
            <br />
            Runs only after you have accepted at least one task from a message and nothing
            is left to review. Many servers move it to Trash, but some erase it outright,
            on those it cannot be recovered.
          </span>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={submit} disabled={!canSave} loading={save.isPending}>
          {data.configured ? 'Save changes' : 'Connect mailbox'}
        </Button>

        {data.configured && (
          <>
            <Button
              variant="secondary"
              onClick={handleTest}
              loading={test.isPending}
            >
              <Plug size={14} strokeWidth={1.5} aria-hidden />
              Test connection
            </Button>
            <Button
              variant="secondary"
              onClick={() => poll.mutate()}
              loading={poll.isPending}
              disabled={!data.enabled}
            >
              <RefreshCw size={14} strokeWidth={1.5} aria-hidden />
              Check now
            </Button>
            <Button
              variant="ghost"
              onClick={() => remove.mutate()}
              loading={remove.isPending}
            >
              <Trash2 size={14} strokeWidth={1.5} aria-hidden />
              Disconnect
            </Button>
          </>
        )}
      </div>

      {data.last_synced_at && (
        <p className="text-small text-text-tertiary">
          Last checked{' '}
          {new Date(data.last_synced_at).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
          .
        </p>
      )}

      {data.last_poll_summary && (
        <p className="text-small text-text-secondary">{data.last_poll_summary}</p>
      )}
    </section>
  );
}
